import dayjs from 'dayjs'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2, Edit3, Trash2 } from 'blaze-ui/icons/20'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { CONTENT_CATEGORY_CONFIG, TopicRow } from './Types'
import Styles from './PostsDetails.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Skeleton } from 'antd'
import { BrandPlanSchedule, ReferenceMediaFile } from 'types'
import { useCreateScheduleReferenceImage } from 'hooks/queries/schedules/useCreateScheduleReferenceImage'
import { Key, useCallback, useEffect, useState } from 'react'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import cx from 'classnames'
import { getDefaultTacticsForPostType } from './utils'
import { ContentTypeSelect } from 'screens/Schedule/PostPromptsSection/ContentTypeSelect'
import {
  CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY,
  CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY,
  ContentCategoryKey,
} from 'screens/Schedule/PostPromptsSection/utils'
import { ReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'
import { ReferenceImageButton } from 'screens/Schedule/PostPromptsSection/ReferenceImageButton'
import { useIsStrictBrandControl } from 'hooks/useIsStrictBrandControl'

export interface PostRowProps {
  row: TopicRow
  index: number
  targetSchedule: BrandPlanSchedule
  onUpdateRow: (id: string, updates: Partial<TopicRow>) => void
  onRemoveRow: (id: string) => void
  canRemove: boolean
}

export const PostRow = ({ row, index, targetSchedule, onUpdateRow, onRemoveRow, canRemove }: PostRowProps) => {
  const [isEditingTopic, setIsEditingTopic] = useState(false)
  const [fetchedReferenceImage, setFetchedReferenceImage] = useState(false)
  const [referenceImage, setReferenceImage] = useState<ReferenceMediaFile | null>(null)
  const isStrictBrandControl = useIsStrictBrandControl()
  const tomorrow = dayjs().add(1, 'day').startOf('day')
  const disableDatesBefore = tomorrow.format('YYYY-MM-DD')
  const campaignEnd = dayjs(targetSchedule.end_date)
  const disableDatesAfter = campaignEnd.isAfter(tomorrow) ? campaignEnd.format('YYYY-MM-DD') : undefined
  const indexLabel = index + 1 < 10 ? `0${index + 1}` : index + 1

  const { mutate: createReferenceImage, isError: errorFetchingReferenceImage } = useCreateScheduleReferenceImage({
    onMutate: () => {
      setReferenceImage(null)
    },
    onSuccess: (data) => {
      setReferenceImage(data)
      onUpdateRow(row.id, { mediaFileIds: [data.key] })
    },
  })

  // Strip trailing newlines from generated/prefilled topics only; skip once the user has edited.
  useEffect(() => {
    if (row.loading || row.userEdited) return

    const trimmed = row.topic.replace(/\n+$/, '')
    if (trimmed === row.topic) return

    onUpdateRow(row.id, { topic: trimmed })
  }, [row.loading, row.userEdited, row.topic, row.id, onUpdateRow])

  const refetchReferenceImage = useCallback(async () => {
    createReferenceImage({ scheduleId: targetSchedule.id, topic: row.topic })
  }, [createReferenceImage, row.topic, targetSchedule.id])

  const handleChangeReferenceImage = useCallback(
    (mediaFile: ReferenceMediaFile) => {
      setReferenceImage(mediaFile)
      onUpdateRow(row.id, { mediaFileIds: [mediaFile.id] })
    },
    [row.id, onUpdateRow],
  )

  useEffect(() => {
    if (fetchedReferenceImage || row.loading) return
    setFetchedReferenceImage(true)

    refetchReferenceImage()
  }, [row.topic, fetchedReferenceImage, row.loading, refetchReferenceImage])

  return (
    <div key={row.id} className={Styles.postRow}>
      {referenceImage?.url ? (
        <div className={Styles.imageWrapper}>
          <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
            <ReferenceImageButton referenceMediaFile={referenceImage} />
          </ReferenceImageTrigger>
        </div>
      ) : (
        <>
          {errorFetchingReferenceImage ? (
            <div className={Styles.imageWrapper}>
              <Text variant="label" color="var(--dark-60)">
                Error fetching reference image
              </Text>
              <div className={Styles.retryButton}>
                <Button variant="secondary" size="sm" frontIcon={ArrowRefresh2} onPress={refetchReferenceImage}>
                  Retry
                </Button>
              </div>
              <div className={Styles.referenceImageButton}>
                <ReferenceImageTrigger onUse={handleChangeReferenceImage} options={{ newStack: true }}>
                  <Button variant="secondary" size="sm" frontIcon={Edit3}>
                    Choose manually
                  </Button>
                </ReferenceImageTrigger>
              </div>
            </div>
          ) : (
            <LoadingShimmerer className={Styles.imageShimmerer}>
              <div className={Styles.imageShimmerer} />
            </LoadingShimmerer>
          )}
        </>
      )}

      <div className={cx(Styles.postData, { [Styles.isLoading]: row.loading })}>
        <div className={Styles.info}>
          {row.loading ? (
            <div className={Styles.header}>
              <Skeleton
                active
                title={{ width: '75px', style: { borderRadius: '6px', height: '21px', marginTop: '0' } }}
                paragraph={false}
                className={Styles.headerSkeleton}
              />
              <Skeleton
                active
                title={{ width: '150px', style: { borderRadius: '6px', height: '21px', marginTop: '0' } }}
                paragraph={false}
                className={Styles.headerSkeleton}
              />
            </div>
          ) : (
            <div className={Styles.header}>
              <Heading level={5}>Post {indexLabel}</Heading>
              <div className={Styles.datePicker}>
                <DateTimePicker
                  dateTime={row.postAt}
                  setDateTime={(dateTime) => {
                    if (!dateTime) return
                    onUpdateRow(row.id, { postAt: dateTime })
                  }}
                  disableDatesBefore={disableDatesBefore}
                  disableDatesAfter={disableDatesAfter}
                >
                  <Button withChevron="down" variant="tertiary" size="sm">
                    {row.postAt.format('MMM D, h:mma')}
                  </Button>
                </DateTimePicker>
              </div>
            </div>
          )}
          <div className={Styles.topic}>
            {!isEditingTopic && !row.loading && (
              <Text variant="label" color="var(--dark-60)">
                Topic:
              </Text>
            )}
            {row.loading ? (
              <Skeleton active paragraph={{ rows: 3 }} title={false} className={Styles.topicSkeleton} />
            ) : (
              <Input
                className={cx(Styles.topicTextarea, { [Styles.editing]: isEditingTopic })}
                placeholder="Enter a topic..."
                isTextarea
                name="topic"
                value={row.topic}
                onFocus={() => setIsEditingTopic(true)}
                onBlur={() => setIsEditingTopic(false)}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  onUpdateRow(row.id, { topic: e.target.value, userEdited: true })
                }
              />
            )}
          </div>
        </div>

        {row.loading ? (
          <div className={Styles.actions}>
            <Skeleton
              active
              className={Styles.actionsSkeleton}
              paragraph={false}
              title={{ width: '100px', style: { borderRadius: '5px', height: '27px', marginTop: '0' } }}
            />
            <Skeleton
              active
              className={Styles.actionsSkeleton}
              paragraph={false}
              title={{ width: '100px', style: { borderRadius: '5px', height: '27px', marginTop: '0' } }}
            />
          </div>
        ) : (
          <div className={Styles.actions}>
            <ContentTypeSelect
              selectedKey={CONTENT_CATEGORY_TO_CONTENT_CATEGORY_KEY[row.contentCategory || 'still_image_post']}
              isDisabled={row.loading}
              isStrictBrandControl={isStrictBrandControl}
              variant="secondary"
              size="sm"
              onSelectionChange={(key: Key) => {
                const contentType = CONTENT_CATEGORY_KEY_TO_CONTENT_CATEGORY[key as ContentCategoryKey]
                const config = CONTENT_CATEGORY_CONFIG[contentType]
                onUpdateRow(row.id, {
                  contentCategory: contentType,
                  postType: config.postType,
                  tactics: getDefaultTacticsForPostType(config.postType, targetSchedule.tactics || []),
                })
              }}
            />
            {canRemove && (
              <Button
                frontIcon={Trash2}
                isDisabled={row.loading}
                variant="secondary"
                size="sm"
                onPress={() => onRemoveRow(row.id)}
              >
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
